class WindowChannel {
    channel = null;
    windowId;
    handlers = new Map();
    constructor() {
        this.windowId = `window-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        this.initChannel();
    }
    initChannel() {
        if (typeof BroadcastChannel === 'undefined') {
            console.warn('[WindowChannel] BroadcastChannel not supported');
            return;
        }
        this.channel = new BroadcastChannel('newlumen-multi-screen');
        this.channel.addEventListener('message', (event) => {
            const message = event.data;
            // Don't handle own messages
            if (message.sourceWindowId === this.windowId)
                return;
            const handlers = this.handlers.get(message.type);
            handlers?.forEach((handler) => handler(message));
        });
    }
    send(type, payload) {
        if (!this.channel)
            return;
        const message = {
            type,
            payload,
            sourceWindowId: this.windowId,
            timestamp: Date.now(),
        };
        this.channel.postMessage(message);
    }
    on(type, handler) {
        if (!this.handlers.has(type)) {
            this.handlers.set(type, new Set());
        }
        this.handlers.get(type).add(handler);
        return () => {
            this.handlers.get(type)?.delete(handler);
        };
    }
    // Convenience methods
    /**
     * Ask a live viewer window to show a study (in-place, no reload).
     * `targetWindowName` addresses ONE viewer of a multi-monitor fleet (e.g.
     * 'newlumen-viewer-2'). Without it the message is honoured by the primary
     * cockpit viewer and by a viewer in a plain tab, while span windows
     * ('newlumen-viewer-N') ignore it — per-monitor hanging protocols must not
     * make the whole fleet jump. Receivers apply both rules in their LOAD_STUDY
     * handler (ViewerShell).
     */
    loadStudy(studyId, opts) {
        this.send('LOAD_STUDY', {
            studyId,
            seriesId: opts?.seriesId,
            caseId: opts?.caseId,
            targetWindowName: opts?.targetWindowName,
        });
    }
    studyLoaded(studyId) {
        this.send('STUDY_LOADED', { studyId });
    }
    /** Ask the live report window to show a report (in-place, no reload). */
    loadReport(studyId, caseId) {
        this.send('LOAD_REPORT', { studyId, caseId });
    }
    /** Broadcast a stack-scroll position for a series (cross-window sync). */
    syncScroll(seriesUid, imageIndex) {
        this.send('SYNC_SCROLL', { seriesUid, imageIndex });
    }
    /** Broadcast a W/L change for a series (cross-window sync). */
    syncWindowLevel(seriesUid, windowWidth, windowCenter) {
        this.send('SYNC_WINDOW_LEVEL', { seriesUid, windowWidth, windowCenter });
    }
    closeViewer() {
        this.send('CLOSE_VIEWER');
    }
    /** Session ended in some window — every window must drop auth state. */
    logout() {
        this.send('LOGOUT');
    }
    ping() {
        this.send('PING');
    }
    pong(payload) {
        this.send('PONG', payload);
    }
    getWindowId() {
        return this.windowId;
    }
    /**
     * Clean up the channel when done
     */
    destroy() {
        this.channel?.close();
        this.channel = null;
        this.handlers.clear();
    }
}
export const windowChannel = new WindowChannel();
